import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Bandage, Plus, Camera, Check, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from './DictableTextarea';
import ConfirmDialog from './ConfirmDialog';
import DatePickerField from '@/components/DatePickerField';

const LIV = { rosso: 'bg-red-100 text-red-700', giallo: 'bg-amber-100 text-amber-700', verde: 'bg-emerald-100 text-emerald-700' };
const OGGI = () => new Date().toISOString().slice(0, 10);
const urlFoto = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/lesioni/foto/${id}/file?auth=${localStorage.getItem('rsa_token')}`;

const nuovaRilevazione = () => ({
  data: OGGI(), categoria: '2', lunghezza_cm: '', larghezza_cm: '', profondita_cm: '',
  essudato: 'Scarso', tipo_essudato: 'Sieroso', tessuto_fondo: 'Granulazione',
  cute_perilesionale: 'Integra', dolore_nrs: 0, segni_infezione: false,
  infezione_descrizione: '', medicazione: '', prossima_medicazione: '', note: '',
});

export default function LesioniCard({ ospiteId, onSaved }) {
  const { hasRole } = useAuth();
  const [dati, setDati] = useState(null);
  const [defs, setDefs] = useState(null);
  const [nuova, setNuova] = useState(null);
  const [ril, setRil] = useState(null);
  const [aperta, setAperta] = useState(null);
  const [files, setFiles] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [daChiudere, setDaChiudere] = useState(null);
  const puoScrivere = hasRole('coordinatore', 'medico', 'infermiere');

  const carica = useCallback(() => {
    api.get(`/lesioni/ospite/${ospiteId}`).then(r => setDati(r.data)).catch(() => {});
  }, [ospiteId]);

  useEffect(() => {
    carica();
    api.get('/lesioni/definizioni').then(r => setDefs(r.data)).catch(() => {});
  }, [carica]);

  const creaLesione = async () => {
    if (!nuova.sede) return toast.error('Indica la sede anatomica');
    setSalvando(true);
    try {
      await api.post('/lesioni', { ...nuova, ospite_id: ospiteId });
      toast.success('Lesione registrata nel registro');
      setNuova(null); carica(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  const salvaRilevazione = async () => {
    setSalvando(true);
    try {
      const payload = {
        ...ril.form,
        lunghezza_cm: parseFloat(ril.form.lunghezza_cm) || 0,
        larghezza_cm: parseFloat(ril.form.larghezza_cm) || 0,
        profondita_cm: parseFloat(ril.form.profondita_cm) || 0,
        dolore_nrs: parseInt(ril.form.dolore_nrs, 10) || 0,
      };
      const r = await api.post(`/lesioni/${ril.lesione.id}/rilevazioni`, payload);
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/lesioni/rilevazioni/${r.data.id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`Rilevazione salvata${files.length ? ` con ${files.length} foto` : ''}`);
      setRil(null); setFiles([]); carica(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  const chiudi = async () => {
    try {
      await api.put(`/lesioni/${daChiudere.id}/chiudi`);
      toast.success('Lesione segnata come guarita');
      setDaChiudere(null); carica(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (!dati || !defs) return null;

  return (
    <Card data-testid="lesioni-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
          <Bandage className="w-4 h-4 text-brand" />Lesioni da pressione
          <Badge variant="outline" className="text-[10px]" data-testid="lesioni-aperte">{dati.aperte} aperte</Badge>
          {dati.braden_ultima && <Badge variant="outline" className="text-[10px]">Braden {dati.braden_ultima.punteggio} · {dati.braden_ultima.classe}</Badge>}
          {puoScrivere && (
            <Button size="sm" className="h-7 text-xs gap-1 ml-auto bg-brand hover:bg-brand-dark text-white"
              onClick={() => setNuova({ sede: '', categoria: '2', data_insorgenza: OGGI(), origine: defs.origine[0], note: '' })}
              data-testid="nuova-lesione-btn"><Plus className="w-3 h-3" />Nuova lesione</Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {dati.lesioni.length === 0 && <p className="text-xs text-emerald-700">Nessuna lesione da pressione registrata.</p>}
        {dati.lesioni.map(l => {
          const open = aperta === l.id;
          const u = l.ultima_rilevazione;
          return (
            <div key={l.id} className={`rounded-lg border ${l.stato === 'guarita' ? 'bg-slate-50' : ''}`} data-testid={`lesione-${l.id}`}>
              <button className="w-full flex items-center justify-between gap-3 p-3 text-left" onClick={() => setAperta(open ? null : l.id)}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{l.sede}</p>
                  <p className="text-[11px] text-slate-500">{l.categoria_label} · insorta il {new Date(l.data_insorgenza).toLocaleDateString('it-IT')} · {l.origine}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {l.stato === 'guarita'
                    ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700">guarita</Badge>
                    : <Badge className={`text-[10px] ${LIV[l.livello]}`}>aperta</Badge>}
                  {l.andamento && <Badge variant="outline" className="text-[10px]">{l.andamento}</Badge>}
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {open && (
                <div className="px-3 pb-3 space-y-3">
                  {u && (
                    <div className="text-xs text-slate-600 space-y-1 bg-slate-50 rounded-lg p-2.5">
                      <p><b>Ultima rilevazione</b> del {new Date(u.data).toLocaleDateString('it-IT')} · {u.operatore_nome}</p>
                      <p>Dimensioni: {u.lunghezza_cm} x {u.larghezza_cm} cm (area {u.area_cm2} cm²) · profondita {u.profondita_cm} cm</p>
                      <p>Essudato: {u.essudato}{u.tipo_essudato ? ` (${u.tipo_essudato})` : ''} · fondo: {u.tessuto_fondo} · cute perilesionale: {u.cute_perilesionale}</p>
                      <p>Dolore NRS: {u.dolore_nrs}/10{u.segni_infezione ? ` · SEGNI DI INFEZIONE: ${u.infezione_descrizione || 'presenti'}` : ''}</p>
                      {u.medicazione && <p>Medicazione: {u.medicazione}{u.prossima_medicazione ? ` · prossima il ${u.prossima_medicazione}` : ''}</p>}
                    </div>
                  )}
                  {l.rilevazioni.some(r => r.foto?.length) && (
                    <div>
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Decorso fotografico (dal piu recente)</p>
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {l.rilevazioni.flatMap(r => (r.foto || []).map(f => ({ ...f, data: r.data }))).map(f => (
                          <a key={f.id} href={urlFoto(f.id)} target="_blank" rel="noreferrer" className="shrink-0 group" data-testid={`lesione-foto-${f.id}`}>
                            <img src={urlFoto(f.id)} alt={`Lesione ${new Date(f.data).toLocaleDateString('it-IT')}`}
                              className="w-24 h-24 object-cover rounded-lg border border-slate-200 group-hover:border-brand transition-colors" />
                            <p className="text-[10px] text-slate-400 text-center mt-0.5">{new Date(f.data).toLocaleDateString('it-IT')}</p>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500">
                    {l.rilevazioni.length} rilevazioni registrate
                    {l.variazione_area_cm2 !== undefined && ` · variazione area ${l.variazione_area_cm2 > 0 ? '+' : ''}${l.variazione_area_cm2} cm² dalla prima`}
                  </div>
                  {puoScrivere && l.stato === 'aperta' && (
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" className="h-8 text-xs gap-1 bg-brand hover:bg-brand-dark text-white"
                        onClick={() => { setRil({ lesione: l, form: { ...nuovaRilevazione(), categoria: l.categoria } }); setFiles([]); }}
                        data-testid={`nuova-rilevazione-${l.id}`}><Camera className="w-3 h-3" />Nuova medicazione</Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setDaChiudere(l)} data-testid={`chiudi-lesione-${l.id}`}>
                        <Check className="w-3 h-3" />Segna come guarita
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!nuova} onOpenChange={(o) => !o && setNuova(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuova lesione da pressione</DialogTitle></DialogHeader>
          {nuova && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Sede anatomica *</Label>
                  <Select value={nuova.sede} onValueChange={v => setNuova(n => ({ ...n, sede: v }))}>
                    <SelectTrigger data-testid="lesione-sede"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{defs.sedi.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Data di insorgenza *</Label>
                  <DatePickerField value={nuova.data_insorgenza} onChange={v => setNuova(n => ({ ...n, data_insorgenza: v }))} testId="lesione-data" /></div>
              </div>
              <div><Label className="text-xs">Stadiazione EPUAP/NPIAP *</Label>
                <Select value={nuova.categoria} onValueChange={v => setNuova(n => ({ ...n, categoria: v }))}>
                  <SelectTrigger data-testid="lesione-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{defs.categorie.map(c => <SelectItem key={c.valore} value={c.valore}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Origine</Label>
                <Select value={nuova.origine} onValueChange={v => setNuova(n => ({ ...n, origine: v }))}>
                  <SelectTrigger data-testid="lesione-origine"><SelectValue /></SelectTrigger>
                  <SelectContent>{defs.origine.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Note</Label>
                <DictableTextarea rows={2} value={nuova.note} onChange={e => setNuova(n => ({ ...n, note: e.target.value }))} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setNuova(null)}>Annulla</Button>
                <Button onClick={creaLesione} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="salva-lesione">
                  {salvando ? 'Salvataggio...' : 'Registra lesione'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!ril} onOpenChange={(o) => !o && setRil(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Medicazione e rilevazione · {ril?.lesione.sede}</DialogTitle></DialogHeader>
          {ril && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Data *</Label>
                  <DatePickerField value={ril.form.data} onChange={v => setRil(r => ({ ...r, form: { ...r.form, data: v } }))} testId="ril-data" /></div>
                <div><Label className="text-xs">Stadiazione attuale *</Label>
                  <Select value={ril.form.categoria} onValueChange={v => setRil(r => ({ ...r, form: { ...r.form, categoria: v } }))}>
                    <SelectTrigger data-testid="ril-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.categorie.map(c => <SelectItem key={c.valore} value={c.valore}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Lunghezza (cm)</Label>
                  <Input type="number" step="0.1" value={ril.form.lunghezza_cm} onChange={e => setRil(r => ({ ...r, form: { ...r.form, lunghezza_cm: e.target.value } }))} data-testid="ril-lunghezza" /></div>
                <div><Label className="text-xs">Larghezza (cm)</Label>
                  <Input type="number" step="0.1" value={ril.form.larghezza_cm} onChange={e => setRil(r => ({ ...r, form: { ...r.form, larghezza_cm: e.target.value } }))} data-testid="ril-larghezza" /></div>
                <div><Label className="text-xs">Profondita (cm)</Label>
                  <Input type="number" step="0.1" value={ril.form.profondita_cm} onChange={e => setRil(r => ({ ...r, form: { ...r.form, profondita_cm: e.target.value } }))} data-testid="ril-profondita" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Essudato</Label>
                  <Select value={ril.form.essudato} onValueChange={v => setRil(r => ({ ...r, form: { ...r.form, essudato: v } }))}>
                    <SelectTrigger data-testid="ril-essudato"><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.essudato.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Tipo di essudato</Label>
                  <Select value={ril.form.tipo_essudato} onValueChange={v => setRil(r => ({ ...r, form: { ...r.form, tipo_essudato: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.tipo_essudato.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Tessuto del fondo</Label>
                  <Select value={ril.form.tessuto_fondo} onValueChange={v => setRil(r => ({ ...r, form: { ...r.form, tessuto_fondo: v } }))}>
                    <SelectTrigger data-testid="ril-tessuto"><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.tessuto_fondo.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Cute perilesionale</Label>
                  <Select value={ril.form.cute_perilesionale} onValueChange={v => setRil(r => ({ ...r, form: { ...r.form, cute_perilesionale: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{defs.cute_perilesionale.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-center">
                <div><Label className="text-xs">Dolore NRS (0-10)</Label>
                  <Input type="number" min="0" max="10" value={ril.form.dolore_nrs} onChange={e => setRil(r => ({ ...r, form: { ...r.form, dolore_nrs: e.target.value } }))} data-testid="ril-dolore" /></div>
                <label className="flex items-center justify-between gap-2 text-xs text-slate-600 mt-4 bg-slate-50 rounded-lg px-3 py-2">
                  Segni di infezione
                  <Switch checked={ril.form.segni_infezione} onCheckedChange={v => setRil(r => ({ ...r, form: { ...r.form, segni_infezione: v } }))} data-testid="ril-infezione" />
                </label>
              </div>
              {ril.form.segni_infezione && (
                <Input placeholder="Descrizione dei segni (arrossamento, calore, odore, pus...)" value={ril.form.infezione_descrizione}
                  onChange={e => setRil(r => ({ ...r, form: { ...r.form, infezione_descrizione: e.target.value } }))} />
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Medicazione utilizzata</Label>
                  <Input value={ril.form.medicazione} onChange={e => setRil(r => ({ ...r, form: { ...r.form, medicazione: e.target.value } }))} placeholder="Idrocolloide, schiuma, alginato..." data-testid="ril-medicazione" /></div>
                <div><Label className="text-xs">Prossima medicazione</Label>
                  <DatePickerField value={ril.form.prossima_medicazione} onChange={v => setRil(r => ({ ...r, form: { ...r.form, prossima_medicazione: v } }))} /></div>
              </div>
              <div><Label className="text-xs">Note</Label>
                <DictableTextarea rows={2} value={ril.form.note} onChange={e => setRil(r => ({ ...r, form: { ...r.form, note: e.target.value } }))} /></div>
              <div>
                <Label className="text-xs">Fotografie (JPG, PNG o WEBP)</Label>
                <Input type="file" accept="image/jpeg,image/png,image/webp" multiple
                  onChange={e => setFiles(Array.from(e.target.files || []))} data-testid="ril-foto" />
                {files.length > 0 && <p className="text-[11px] text-slate-500 mt-1">{files.length} immagini selezionate</p>}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRil(null)}>Annulla</Button>
                <Button onClick={salvaRilevazione} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="salva-rilevazione">
                  {salvando ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Salvataggio...</> : 'Salva rilevazione'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!daChiudere} onClose={() => setDaChiudere(null)}
        title="Segnare la lesione come guarita?" message="La lesione resta in archivio con la data di guarigione."
        onConfirm={chiudi} />
    </Card>
  );
}
